import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { Subscription } from 'rxjs';
import {
  HistoryCategory,
  HistoryValue,
  WorkOrderHistoryChange,
  WorkOrderHistoryEvent,
  WorkOrderHistoryService,
} from './work-order-history.service';

interface HistoryGroup {
  key: string;
  actor: string;
  verified: boolean;
  changedAt: string;
  source: string;
  events: WorkOrderHistoryEvent[];
}

@Component({
  selector: 'app-work-order-history',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './work-order-history.component.html',
  styleUrls: ['./work-order-history.component.scss'],
})
export class WorkOrderHistoryComponent implements OnChanges, OnDestroy {
  @Input() ladingUuid: string | null = null;
  @Input() active = true;
  @Input() financialsAllowed = false;

  category: HistoryCategory = 'all';
  loading = false;
  error = '';
  coverage = '';
  groups: HistoryGroup[] = [];
  nextCursor: string | null = null;

  private serverAllowsFinancials = true;
  private events: WorkOrderHistoryEvent[] = [];
  private request?: Subscription;
  private requestSequence = 0;

  constructor(private readonly history: WorkOrderHistoryService) {}

  get showFinancials(): boolean {
    // Jenny: the parent screen and API must both allow financial visibility.
    // This controls the UI; the PHP helper and API enforce the actual access rules.
    return this.financialsAllowed && this.serverAllowsFinancials;
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.cancelPendingRequest();

    if (changes['ladingUuid'] || changes['financialsAllowed']) {
      this.clearHistory();
      this.error = '';

      if (!this.financialsAllowed && this.category === 'financial') {
        this.category = 'all';
      }
    }

    if (this.active && this.ladingUuid) {
      this.load(true);
    }
  }

  ngOnDestroy(): void {
    this.cancelPendingRequest();
  }

  selectCategory(category: HistoryCategory): void {
    if (category === this.category) {
      return;
    }
    if (category === 'financial' && !this.showFinancials) {
      return;
    }

    this.category = category;
    this.load(true);
  }

  refresh(): void {
    this.load(true);
  }

  older(): void {
    if (this.nextCursor && !this.loading) {
      this.load(false);
    }
  }

  private load(reset: boolean): void {
    if (!this.ladingUuid || !this.active) {
      return;
    }

    this.cancelPendingRequest();
    const requestSequence = this.requestSequence;
    this.loading = true;
    this.error = '';

    if (reset) {
      this.clearHistory();
    }

    const cursor = reset ? null : this.nextCursor;
    this.request = this.history.load(this.ladingUuid, cursor, this.category).subscribe({
      next: page => {
        // Jenny: a response from the previous work order/filter must never
        // replace the current view, even if it arrives after cancellation.
        if (requestSequence !== this.requestSequence) {
          return;
        }
        if (!page.ok) {
          this.error = 'Work-order history could not be loaded.';
          this.loading = false;
          return;
        }

        this.serverAllowsFinancials = page.can_view_financials;

        // Permissions can change between pages. Purge previously loaded money
        // entries as well as filtering the incoming page when access is removed.
        if (!this.serverAllowsFinancials) {
          this.events = this.events.filter(event => event.category !== 'financial');
        }

        const knownEventIds = new Set(this.events.map(event => event.id));
        const newEvents = page.events.filter(event => {
          if (knownEventIds.has(event.id)) {
            return false;
          }

          return this.serverAllowsFinancials || event.category !== 'financial';
        });

        this.events.push(...newEvents);
        this.nextCursor = page.next_cursor;
        this.coverage = page.coverage;
        this.groups = this.groupEvents(this.events);
        this.loading = false;
      },
      error: () => {
        if (requestSequence !== this.requestSequence) {
          return;
        }

        this.error = 'Work-order history could not be loaded. Refresh to try again.';
        this.loading = false;
      },
    });
  }

  private clearHistory(): void {
    this.events = [];
    this.groups = [];
    this.nextCursor = null;
  }

  private groupEvents(events: WorkOrderHistoryEvent[]): HistoryGroup[] {
    const groups = new Map<string, HistoryGroup>();

    for (const event of events) {
      // Jenny: one save can change several child rows. Prefer its operation ID,
      // then request ID, then the individual event ID when neither is available.
      // Actor and source stay in the key so unrelated people are never merged.
      const operationKey = event.operation_id || event.request_id || event.id;
      const key = [
        operationKey,
        event.actor.label,
        event.actor.verified,
        event.source,
      ].join(':');

      let group = groups.get(key);
      if (!group) {
        group = {
          key,
          actor: event.actor.label,
          verified: event.actor.verified,
          changedAt: event.changed_at,
          source: event.source,
          events: [],
        };
        groups.set(key, group);
      }

      group.events.push(event);
    }

    return [...groups.values()];
  }

  summaryEvents(events: WorkOrderHistoryEvent[]): WorkOrderHistoryEvent[] {
    const firstEventByEntity = new Map<string, WorkOrderHistoryEvent>();

    for (const event of events) {
      if (!firstEventByEntity.has(event.entity)) {
        firstEventByEntity.set(event.entity, event);
      }
    }

    // Keep the collapsed summary short; expanding it still shows every event.
    return [...firstEventByEntity.values()].slice(0, 3);
  }

  action(event: WorkOrderHistoryEvent): string {
    switch (event.operation) {
      case 'INSERT':
        return 'Added';
      case 'DELETE':
        return 'Deleted';
      default:
        return 'Changed';
    }
  }

  value(value: HistoryValue, change: WorkOrderHistoryChange): string {
    if (value === null) {
      return '—';
    }

    // Only the existing financial formatter marks integer cents as money.
    // Other numeric detail fields already use their stored/display units.
    if (change.kind === 'money' && Number.isFinite(Number(value))) {
      return (Number(value) / 100).toFixed(2);
    }
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }

    return String(value);
  }

  trackGroup(_index: number, group: HistoryGroup): string {
    return group.key;
  }

  trackEvent(_index: number, event: WorkOrderHistoryEvent): string {
    return event.id;
  }

  private cancelPendingRequest(): void {
    this.requestSequence++;
    this.request?.unsubscribe();
    this.loading = false;
  }
}
